import React, { useState } from "react";
import { getRecaptchaToken } from "../../lib/recaptcha";
import { logAnalyticsEvent } from "../../lib/analytics";

type State = "idle" | "submitting" | "success" | "error";

/**
 * SubscribeForm — posts the email to /api/subscribe (served by the
 * Cloud Function). On success the user receives an automated
 * acknowledgement via Resend, so the UI only needs a thank-you line.
 */
export function SubscribeForm({ source = "site" }: { source?: string }) {
  const [email, setEmail] = useState("");
  // Honeypot field — must remain empty. Bots that auto-fill every input will
  // populate this and be silently rejected server-side.
  const [company, setCompany] = useState("");
  const [state, setState] = useState<State>("idle");
  const [message, setMessage] = useState<string>("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email || state === "submitting") return;
    setState("submitting");
    try {
      const recaptchaToken = await getRecaptchaToken("subscribe");
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source, company, recaptchaToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setState("success");
      setMessage(
        "Thank you. A confirmation is on its way to your inbox. If it doesn't arrive within a few minutes, check spam."
      );
      setEmail("");
      void logAnalyticsEvent("subscribe_submit", {
        result: "success",
        source,
      });
    } catch (err: unknown) {
      setState("error");
      const errorMessage =
        err instanceof Error ? err.message : "unknown_error";
      setMessage(
        err instanceof Error
          ? `Something went wrong: ${err.message}`
          : "Something went wrong. Please try again shortly."
      );
      void logAnalyticsEvent("subscribe_submit", {
        result: "error",
        source,
        error: errorMessage,
      });
    }
  }

  return (
    <form onSubmit={onSubmit} className="w-full max-w-md mx-auto" aria-live="polite">
      {/* Honeypot — visually hidden, off the tab order, ignored by humans. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}>
        <label htmlFor="subscribe-company">Company</label>
        <input
          id="subscribe-company"
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
      </div>
      <label htmlFor="subscribe-email" className="sr-only">
        Email address
      </label>
      <div className="flex">
        <input
          id="subscribe-email"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={state === "submitting" || state === "success"}
          className="flex-1 bg-paper-2 border border-hairline px-4 py-2 font-mono text-sm text-ink focus:outline-none focus:border-ink placeholder:text-ink-3"
        />
        <button
          type="submit"
          disabled={state === "submitting" || state === "success"}
          className="bg-ink text-paper px-6 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-ink-2 transition-colors disabled:opacity-60"
        >
          {state === "submitting" ? "Sending\u2026" : state === "success" ? "Subscribed" : "Subscribe"}
        </button>
      </div>
      {state !== "idle" && message && (
        <p
          className={`mt-3 text-xs font-mono ${
            state === "error" ? "text-state-pending" : "text-ink-2"
          }`}
        >
          {message}
        </p>
      )}
    </form>
  );
}
